/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createSubscriber,
	createContainer,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
	type SubscriberComponent,
} from '@atlassian/react-sweet-state';
import type { BreadcrumbStateData, State } from './types.tsx';

const initialState: State = { trail: [] };

export const actions = {
	pushBreadcrumb:
		(
			breadcrumb: BreadcrumbStateData,
		): (({
			setState,
			getState,
		}: {
			setState: (state: State) => void;
			getState: () => State;
		}) => void) =>
		({ setState, getState }) => {
			const { trail } = getState();
			if (breadcrumb.id && !trail.some((item) => item.id === breadcrumb.id)) {
				setState({
					trail: [...trail, breadcrumb],
				});
			}
		},
	popBreadcrumb:
		(): (({
			setState,
			getState,
		}: {
			setState: (state: State) => void;
			getState: () => State;
		}) => void) =>
		({ setState, getState }) => {
			setState({
				trail: getState().trail.slice(0, -1),
			});
		},
	updateBreadcrumb:
		(
			breadcrumb: BreadcrumbStateData,
		): (({
			setState,
			getState,
		}: {
			setState: (state: State) => void;
			getState: () => State;
		}) => void) =>
		({ setState, getState }) => {
			const newTrail = getState().trail.map((item) =>
				item.id === breadcrumb.id ? breadcrumb : item,
			);
			setState({
				trail: newTrail,
			});
		},
} as const;

type Actions = typeof actions;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type ContainerProps = Record<any, any>;

const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'BreadcrumbSweetState',
});

const _BreadcrumbSubscriber = createSubscriber<State, Actions>(Store);
export const useBreadcrumbs: HookFunction<State, BoundActions<State, Actions>, void> = createHook(
	Store,
);

export const BreadcrumbActions: SubscriberComponent<
	State,
	BoundActions<
		State,
		{
			readonly pushBreadcrumb: (
				breadcrumb: BreadcrumbStateData,
			) => ({
				setState,
				getState,
			}: {
				setState: (state: State) => void;
				getState: () => State;
			}) => void;
			readonly popBreadcrumb: () => ({
				setState,
				getState,
			}: {
				setState: (state: State) => void;
				getState: () => State;
			}) => void;
			readonly updateBreadcrumb: (
				breadcrumb: BreadcrumbStateData,
			) => ({
				setState,
				getState,
			}: {
				setState: (state: State) => void;
				getState: () => State;
			}) => void;
		}
	>,
	unknown
> = createSubscriber<State, Actions>(Store, {
	selector: null,
});

export const BreadcrumbContainer: OverrideContainerComponent<ContainerProps> = createContainer<
	State,
	Actions,
	ContainerProps
>(Store);
